import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Form,
  Popover,
  Space,
  Switch,
  Tabs,
  Typography,
} from 'antd';
import Table, { ColumnsType } from 'antd/lib/table';
import { FilterOutlined } from '@ant-design/icons';

import {
  DEFAULT_FONT_SIZE,
  DEFAULT_SIZE_TYPE,
  EXTENDED_TIMEOUT,
  GENERAL_TIMEOUT,
} from '../../../constants/systemConstants';
import {
  FontSizeType,
  PrintType,
  StatementPrintoutOrderData,
  UserMonthlyStatementData,
} from '../../../types';
import { getDataWithAuthToken } from '../../../utils/axiosRequest';
import { setFont } from '../../../utils/colComponents';
import { alertMessage } from '../../../utils/alertMessage';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { hasPermission } from '../../../utils/hasPermission';
import { actionPermissions } from '../../../constants/actionPermissions';
import getDashboardStyle from '../../../utils/getDashboardStyle';
import TableToolbar from '../../table/TableToolbar';
import SellersDropdown from '../../sellers/SellersDropdown';
import PrintModal from '../../PrintModal';

type Props = { id?: string };

/**
 * 用户详情 → 月度账单 Tab
 * 数据源: GET /analysis/user/monthly_statement
 * 操作 → 查看: 拿这行 startDate/endDate 调 /order/print_statement, 弹 PrintModal (mode=statement)
 *           跟 analysis/user/statement 的 "View" 完全一致 (支持发送邮件/打印)
 */
const MonthlyStatementTable = ({ id }: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // ====== 列表 / 分页 ======
  const [list, setList] = useState<UserMonthlyStatementData[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage('pageSize', '10');
  const [total, setTotal] = useState(0);

  // ====== 表格控件 ======
  const [fontSize, setFontSize] = useState<FontSizeType>(DEFAULT_FONT_SIZE);
  const [tableSize, setTableSize] = useState<SizeType>(DEFAULT_SIZE_TYPE);
  const columnKeys: string[] = [
    'period',
    'totalOrderNum',
    'totalStatementAmount',
    'actions',
  ];
  const [selectedColumns, setSelectedColumns] = useState<string[]>(columnKeys);

  // ====== 过滤状态 ======
  const [filterByExpect, setFilterByExpect] = useState(true);
  const [isSeller, setIsSeller] = useState(false);
  const [advance, setAdvance] = useState<{ [key: string]: any }>({});
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();

  // ====== Print Modal ======
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printModalLoading, setPrintModalLoading] = useState(false);
  const [printData, setPrintData] = useState<PrintType | undefined>();

  const fetchList = useCallback(() => {
    if (!id) return;
    setLoading(true);
    getDataWithAuthToken('analysis/user/monthly_statement', {
      params: {
        userId: id,
        filterByExpectShippingTime: filterByExpect,
        isSeller,
        sellerId:
          isSeller && form.getFieldValue('sellerId')
            ? form.getFieldValue('sellerId')
            : undefined,
        page,
        size: pageSize,
      },
    })
      .then((response) => {
        setLoading(false);
        if (response && response.goodStatus) {
          setList(response.data.list);
          setTotal(response.data.totalItem);
        } else if (response) {
          alertMessage(
            'error',
            response.msg || t('general.noResponse'),
            response.data || undefined
          );
        }
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [id, filterByExpect, isSeller, page, pageSize, form, t]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // isSeller 切换关闭时清掉 sellerId
  useEffect(() => {
    if (!isSeller) {
      form.resetFields(['sellerId']);
      setAdvance((prev) => {
        const { sellerId, ...rest } = prev;
        return rest;
      });
    }
  }, [isSeller, form]);

  /**
   * 拉指定月份的 print_statement 数据, 弹模态预览
   * 跟 analysis/user/UserStatement.tsx 的 getStatementPrintoutContents 完全一致
   */
  const openPreview = (row: UserMonthlyStatementData) => {
    if (!id) return;
    setPrintModalVisible(true);
    setPrintModalLoading(true);

    const params: { [key: string]: any } = {
      userId: id,
      isSeller,
      sellerId:
        isSeller && form.getFieldValue('sellerId')
          ? form.getFieldValue('sellerId')
          : undefined,
    };
    // 按当前 Radio 决定走 expectShipping 还是 addTime 字段, 跟列表口径保持一致
    if (filterByExpect) {
      params.startExpectShippingTime = row.startDate;
      params.endExpectShippingTime = row.endDate;
    } else {
      params.startAddTime = row.startDate + ' 00:00:00';
      params.endAddTime = row.endDate + ' 23:59:59';
    }

    getDataWithAuthToken('order/print_statement', { params })
      .then((response) => {
        setPrintModalLoading(false);
        if (response && response.goodStatus) {
          const stmt: StatementPrintoutOrderData = response.data;
          setPrintData({
            userId: Number(id),
            statementData: {
              ...stmt,
              startDate: row.startDate,
              endDate: row.endDate,
            },
            orderClaimsData: stmt.orderClaims ? stmt.orderClaims : undefined,
          });
        } else {
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
      })
      .catch((err) => {
        console.log(err);
        setPrintModalLoading(false);
      });
  };

  const fmtPeriod = (p: string) =>
    p && p.length === 6 ? `${p.substring(0, 4)}-${p.substring(4, 6)}` : p;

  const columns: ColumnsType<UserMonthlyStatementData> = [
    {
      title: setFont(t('users.monthlyStatement.period'), fontSize),
      dataIndex: 'period',
      key: 'period',
      width: 100,
      render: (p: string, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong style={{ fontSize }}>
            {fmtPeriod(p)}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {record.startDate} ~ {record.endDate}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: setFont(t('users.monthlyStatement.totalOrderNum'), fontSize),
      dataIndex: 'totalOrderNum',
      key: 'totalOrderNum',
      width: 120,
      align: 'right',
      render: (n: number) => setFont(String(n), fontSize),
    },
    {
      title: setFont(t('users.monthlyStatement.totalStatementAmount'), fontSize),
      dataIndex: 'totalStatementAmount',
      key: 'totalStatementAmount',
      width: 120,
      align: 'right',
      render: (a: string) => (
        <Typography.Text strong style={{ fontSize }}>
          {a}
        </Typography.Text>
      ),
    },
    {
      title: setFont(t('actionsColumn.title'), fontSize),
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (record: UserMonthlyStatementData) => (
        <Button
          type="link"
          style={{ padding: 0, fontSize }}
          disabled={!hasPermission(actionPermissions.userGroup.userView)}
          onClick={() => openPreview(record)}
        >
          {t('actionsColumn.view')}
        </Button>
      ),
    },
  ];

  // ====== 高级搜索 Popover (isSeller=true 时显示 sellerDropdown) ======
  const advancedSearch = (
    <Popover
      overlayStyle={{ zIndex: 100 }}
      title={t('general.advancedSearch')}
      trigger="click"
      placement="bottomRight"
      content={
        <Form form={form} layout="vertical" style={{ width: 280 }}>
          {isSeller && (
            <Form.Item
              name="sellerId"
              label={t('order.orderListColumns.advancedSearch.sellerId')}
              style={{ marginBottom: 12 }}
            >
              <SellersDropdown
                onChange={(value: string) =>
                  setAdvance((prev) => ({ ...prev, sellerId: value }))
                }
              />
            </Form.Item>
          )}
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  if (page !== 1) setPage(1);
                  else {
                    if (typingTimeout) clearTimeout(typingTimeout);
                    setTypingTimeout(
                      setTimeout(() => fetchList(), GENERAL_TIMEOUT)
                    );
                  }
                }}
              >
                {t('general.search')}
              </Button>
              <Button
                disabled={Object.keys(advance).length === 0}
                onClick={() => {
                  form.resetFields();
                  setAdvance({});
                  if (page !== 1) setPage(1);
                  else fetchList();
                }}
              >
                {t('general.reset')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      }
    >
      <Badge
        count={Object.keys(advance).reduce(
          (acc, k) => (advance[k] ? acc + 1 : acc),
          0
        )}
      >
        <Button icon={<FilterOutlined />}>{t('general.advancedSearch')}</Button>
      </Badge>
    </Popover>
  );

  return (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {t('users.userDetailPage.monthlyStatement')}
        </Typography.Title>
        {/* isSeller: dashboardStyle 控制是否显示 */}
        {getDashboardStyle().isSellerSwitch && (
          <Switch
            loading={loading}
            checked={isSeller}
            onChange={(checked) => {
              if (typingTimeout) clearTimeout(typingTimeout);
              setTypingTimeout(
                setTimeout(() => {
                  setIsSeller(checked);
                  setPage(1);
                }, EXTENDED_TIMEOUT)
              );
            }}
            checkedChildren={t('goods.goodsListColumns.seller')}
            unCheckedChildren={t('goods.goodsListColumns.self')}
          />
        )}
      </Space>
      {/* filterByExpect 用 Tabs 展示 (类似订单列表 navType) */}
      <Tabs
        activeKey={filterByExpect ? 'expect' : 'add'}
        onTabClick={(key) => {
          const next = key === 'expect';
          if (next !== filterByExpect) {
            setFilterByExpect(next);
            setPage(1);
          }
        }}
      >
        <Tabs.TabPane
          tab={t('users.monthlyStatement.byExpectShippingTime')}
          key="expect"
        />
        <Tabs.TabPane
          tab={t('users.monthlyStatement.byAddTime')}
          key="add"
        />
      </Tabs>
      <TableToolbar
        setFontSize={setFontSize}
        fontSize={fontSize}
        tableSize={tableSize}
        setTableSize={setTableSize}
        refresh={fetchList}
        totalItems={total}
        columns={columns}
        columnKeys={columnKeys}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        rows={list}
        exportConfig={{ fileName: 'USER_MONTHLY_STATEMENT' }}
        advancedSearch={isSeller ? advancedSearch : undefined}
      />
      <Table<UserMonthlyStatementData>
        columns={columns.filter((x) =>
          selectedColumns.includes(x.key?.toString() ?? '')
        )}
        dataSource={list}
        rowKey={(r) => r.period}
        loading={loading}
        size={tableSize}
        scroll={{ y: 600, x: 600 }}
        pagination={{
          total,
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (totalCnt, range) =>
            t('general.paginationTotal', {
              start: range[0],
              end: range[1],
              total: totalCnt,
            }),
          size: 'small',
          defaultPageSize: pageSize,
          onChange: (p, pSize) => {
            setPage(p);
            setPageSize(pSize || pageSize);
          },
          current: page,
        }}
      />
      <PrintModal<PrintType>
        modalLoading={printModalLoading}
        mode="statement"
        data={printData}
        setData={setPrintData}
        visible={printModalVisible}
        setVisible={setPrintModalVisible}
      />
    </>
  );
};

export default MonthlyStatementTable;
